import React from 'react'
import { Divider, Theme, createStyles, makeStyles } from '@material-ui/core';
import { Stack, Box, Slider } from '@mui/material';
import { connect, useSelector } from 'react-redux';
import { setConstraints, setDiversity, setLineupDiversity, setLiveContestId, setPercentToFirst, setPoolSize, setRunback, setUseCustomExposures } from './store/actions';
import StackConstraints from '../contestGenerator/StackConstraints';
import { StackConstraint } from './store/types';
import { SlateResponse } from '../common/store/types';
import { mlbStacks } from '../contestGenerator/Settings';
import { percentToFirstOptions, poolSizeOptions, SelectInput } from '../common/selectOptions';
import { useGetLiveContests } from '../common/queries/contestQueries';
import { CustomToolTip, toolTipText } from '../common/CustomToolTip';
import { TooltipSelectLabelStyledTypography } from '../common/Table/_components';
import { SubscriptionTierEnum } from '../common/enums';

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {

  },
  buttonWrapper: {
    color: 'white',
    backgroundColor: '#000710',
    padding: '4px 5px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '4px',
    border: '1px solid rgba(255, 255, 255, 0.20)'
  },
  popover: {
    borderRadius: '6px',
    color: 'white',
  },
  sliderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    '& .MuiSlider-markLabel': {
      color: 'white',
      fontSize: '8px',
    },
  },
  sliderHeader: {
    opacity: 0.7,
    fontFamily: 'Inter',
    fontSize: 12,
  },
  applyButton: {
    background: '#015ACE',
    borderRadius: 6,
    padding: '8px 10px',
    color: 'white',
    '&:hover': {
      background: '#015ACE',
      color: 'white',
    }
  },
})
);

interface OwnProps {
  readonly poolSource: string;
}

interface ReduxProps {
  readonly diversity: number;
  readonly lineupDiversity: number;
  readonly stackConstraints: StackConstraint[];
  readonly runback: StackConstraint;
  readonly generatedSlate: SlateResponse;
  readonly poolSize: number;
  readonly percentToFirst: number;
  readonly liveContestId: string;
  readonly useCustomExposures: boolean;
}

interface DispatchProps {
  readonly SetDiversity: (diversity: number | number[]) => void;
  readonly SetLineupDiversity: (diversity: number | number[]) => void;
  readonly SetConstraints: (constraints: StackConstraint[]) => void;
  readonly SetRunback: (runback: StackConstraint) => void;
  readonly SetPoolSize: (size: number) => void;
  readonly SetPercentToFirst: (percent: number) => void;
  readonly SetLiveContestId: (id: string) => void;
  readonly UseCustomExposures: (useCustomExposure: boolean) => void;
}

type Props = ReduxProps & DispatchProps & OwnProps;


const LineupSettings = (props: Props) => {
  const classes = useStyles();
  const { diversity, lineupDiversity, useCustomExposures, generatedSlate, stackConstraints, runback, poolSize, percentToFirst, poolSource, liveContestId, SetLiveContestId, SetDiversity, SetLineupDiversity, SetConstraints, SetRunback, SetPercentToFirst, SetPoolSize, UseCustomExposures } = props;
  const savedStackConstraintKey = `lateSwap-stackConstraints-${generatedSlate.sport}-${generatedSlate.site}-${generatedSlate.slateId}`;
  const { data: liveContests } = useGetLiveContests(generatedSlate?.slateId?.toString(), generatedSlate?.slateId === undefined);
  const authenticated = useSelector((state: any) => state.common.authenticated);
  const tierLocked = authenticated?.data?.authTier !== SubscriptionTierEnum[SubscriptionTierEnum.SIMS_MAX] &&
    authenticated?.data?.authTier !== SubscriptionTierEnum[SubscriptionTierEnum.MVP];


  const setStackExposures = (newConstraints: StackConstraint[], runback: StackConstraint, useCustomExposure: boolean) => {
    SetConstraints(newConstraints);
    SetRunback(runback);
    UseCustomExposures(useCustomExposure === undefined ? false : useCustomExposure);
  }


  React.useEffect(() => {
    const savedConstraints = localStorage?.getItem(savedStackConstraintKey);
    if (savedConstraints) {
      const parsedSavedConstraints = JSON.parse(savedConstraints)
      setStackExposures(parsedSavedConstraints.constraints, parsedSavedConstraints.runback, parsedSavedConstraints.useCustomExposure);
    } else if (generatedSlate.sport?.toLowerCase() === 'mlb' && generatedSlate.site) {
      setStackExposures(mlbStacks[generatedSlate.site?.toLowerCase()], { primary: '', secondary: 0, percent: 0 }, false);
    }
  }, [generatedSlate])

  React.useEffect(() => {
    const maxEntries = liveContestId ? liveContests?.find(x => x.siteContestId === liveContestId)?.maxEntries : undefined;
    const options = poolSizeOptions(generatedSlate?.sport, generatedSlate?.type?.toLowerCase(), maxEntries, generatedSlate.allowedEntryCounts, authenticated?.data?.authTier);
    if (poolSource === 'LiveContest' && !options.map(x => x.value as number).includes(poolSize)) {
      SetPoolSize(options[0]?.value as number);
    }
  }, [poolSource, liveContestId])

  return (
    <div>
      <Stack direction="row" gap="20px" className={classes.popover}>
        {poolSource === 'LiveContest' && (
          <>
            <Stack direction='column' >
              <SelectInput
                data={liveContests?.map(x => ({ name: x.name, value: x.siteContestId, option: { name: x.name, value: x.siteContestId } })) || []}
                label="Select Live Contest"
                bottomLabel={toolTipText.liveContest}
                width={125}
                value={liveContestId}
                onSelect={(selection: any) => SetLiveContestId(selection.value)}
                tierLock={false}
              />
            </Stack>
            <Stack direction={'column'}>
              <SelectInput
                data={poolSizeOptions(
                  generatedSlate?.sport,
                  generatedSlate?.type?.toLowerCase(),
                  liveContestId ? liveContests?.find(x => x.siteContestId === liveContestId)?.maxEntries : undefined,
                  generatedSlate.allowedEntryCounts,
                  authenticated?.data?.authTier
                )}
                label="Pool Size"
                bottomLabel={toolTipText.poolSize}
                width={125}
                value={poolSize}
                onSelect={(selection: any) => SetPoolSize(selection.value)}
                tierLock={tierLocked}
              />
            </Stack>
          </>
        )}
        {poolSource === 'GeneratePool' && (
          <>
            <Stack direction={'column'}>
              <SelectInput
                data={poolSizeOptions(
                  generatedSlate?.sport,
                  generatedSlate?.type?.toLowerCase(),
                  undefined,
                  generatedSlate.allowedEntryCounts,
                  authenticated?.data?.authTier
                )}
                label="Pool Size"
                bottomLabel={toolTipText.poolSize}
                width={125}
                value={poolSize}
                onSelect={(selection: any) => SetPoolSize(selection.value)}
                tierLock={tierLocked}
              />
            </Stack>
            {generatedSlate.sport?.toLowerCase() === 'mlb' && (
              <Box marginTop={-0.5} display='flex' alignItems='center'>
                <StackConstraints savedStackConstraintKey={savedStackConstraintKey} constraints={stackConstraints} runback={runback} onApply={(newConstraints: StackConstraint[], runback: StackConstraint, useCustomExposure: boolean) => setStackExposures(newConstraints, runback, useCustomExposure)} sport={generatedSlate?.sport} useCustomExposures={useCustomExposures} />
              </Box>
            )}
            {generatedSlate.sport?.toLowerCase() !== 'mlb' && (
              <Stack direction={'column'} width={198} className={classes.sliderWrapper}>
                <CustomToolTip title={toolTipText.contestArchetype}>
                  <TooltipSelectLabelStyledTypography>Contest Archetype</TooltipSelectLabelStyledTypography>
                </CustomToolTip>
                <Box>
                  <Slider
                    defaultValue={diversity}
                    value={diversity}
                    onChange={(e, value) => SetDiversity(value)}
                    min={-2}
                    max={2}
                    aria-labelledby="discrete-slider-custom"
                    step={1}
                    marks={marks}
                    track={false}
                  />
                </Box>
              </Stack>
            )}
            <Box margin="auto 20px" height="41px">
              <Divider orientation="vertical" style={{ backgroundColor: 'white', }} />
            </Box>
          </>
        )}
        <Stack direction={'column'}>
          <SelectInput
            data={percentToFirstOptions}
            label="Percent To First"
            bottomLabel={toolTipText.percentToFirst}
            width={100}
            value={percentToFirst}
            onSelect={(selection: any) => SetPercentToFirst(selection.value)}
            tierLock={false}
          />
        </Stack>
        <Stack direction={'column'} width={198} className={classes.sliderWrapper}>
          <CustomToolTip title={toolTipText.swappableLineupProfile}>
            <TooltipSelectLabelStyledTypography>Swappable Lineup Profile</TooltipSelectLabelStyledTypography>
          </CustomToolTip>
          <Box>
            <Slider
              defaultValue={lineupDiversity}
              value={lineupDiversity}
              onChange={(e, value) => SetLineupDiversity(value)}
              min={-2}
              max={2}
              aria-labelledby="discrete-slider-custom"
              step={1}
              marks={lineupMarks}
              track={false}
            />
          </Box>
        </Stack>
      </Stack>
    </div>
  )
}

const marks = [
  {
    value: -2,
    label: 'Low Stakes',
  },
  {
    value: -1,
    label: '',
  },
  {
    value: 0,
    label: 'Marquee',
  },
  {
    value: 1,
    label: '',
  },
  {
    value: 2,
    label: 'High Stakes',
  },
];

const lineupMarks = [
  {
    value: -2,
    label: 'Contrarian',
  },
  {
    value: -1,
    label: '',
  },
  {
    value: 0,
    label: 'Balanced',
  },
  {
    value: 1,
    label: '',
  },
  {
    value: 2,
    label: 'Chalky',
  },
];

const mapStateToProps = (state: any) => {
  const {
    lineupDiversity,
    diversity,
    generatedSlate,
    stackConstraints,
    runback,
    poolSize,
    percentToFirst,
    liveContestId,
    useCustomExposures,
  } = state.lateSwap;

  return {
    lineupDiversity,
    diversity,
    generatedSlate,
    liveContestId,
    stackConstraints,
    runback,
    poolSize,
    percentToFirst,
    useCustomExposures
  }
}


const mapDispatchToProps = {
  SetDiversity: setDiversity,
  SetLineupDiversity: setLineupDiversity,
  SetConstraints: setConstraints,
  SetRunback: setRunback,
  SetPercentToFirst: setPercentToFirst,
  SetPoolSize: setPoolSize,
  SetLiveContestId: setLiveContestId,
  UseCustomExposures: setUseCustomExposures
}

export default connect(mapStateToProps, mapDispatchToProps)(LineupSettings);